<template>
  <Navbar :title="sidebarTitle" :class="['navbar--wide', {'show': showNavbar}]">
    <template v-slot:close>
      <div @click="close" class="close">Cerrar</div>
    </template>
    <template v-slot:content>
      <!-- Summary header: half-of-month + status, composed as a block -->
      <div v-if="payslip.id" class="ps-section ps-summary">
        <span class="ps-summary__half">{{ halfLabel }}</span>
        <span v-if="payslip.status === 'finalized'" class="payslip-status payslip-status--finalized">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          Pagada
        </span>
        <span v-else-if="payslip.status === 'voided'" class="payslip-status payslip-status--voided">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          Anulada
        </span>
        <span v-else class="payslip-status payslip-status--draft">Borrador</span>
      </div>

      <!-- Voided banner -->
      <div v-if="payslip.status === 'voided'" class="ps-section">
        <div class="ps-banner ps-banner--error">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <span><strong>Boleta anulada</strong><span v-if="payslip.voidReason"> — {{ payslip.voidReason }}</span></span>
        </div>
      </div>

      <!-- Finalized banner -->
      <div v-if="payslip.status === 'finalized'" class="ps-section">
        <div class="ps-banner ps-banner--success">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <span>Pagada el {{ formatDate(payslip.paymentDate) }}<span v-if="payslip.paymentMethod"> · {{ formatMethod(payslip.paymentMethod) }}</span></span>
        </div>
      </div>

      <!-- Read-only summary blocks side by side on wide, stacked on narrow -->
      <div class="ps-grid">
        <!-- Base -->
        <div class="ps-section">
          <div class="ps-calc-cell">
            <div class="ps-calc-cell__label">Base</div>
            <div v-if="payslip.base && payslip.base.kind === 'fixed'" class="ps-base">
              <span class="ps-base__amount">{{ formatAmount(editedBase.amount) }}</span>
              <span class="ps-base__desc">½ sueldo mensual (quincena)</span>
            </div>
            <div v-else-if="payslip.base" class="ps-variable">
              <div class="ps-variable__formula">
                <input
                  v-if="isDraft"
                  v-model.number="editedUnits"
                  @input="onUnitsInput"
                  class="input ps-variable__input"
                  type="number"
                  min="0"
                  step="0.5"
                >
                <span v-else class="ps-variable__input ps-variable__input--static">{{ editedUnits }}</span>
                <span class="ps-variable__sep">{{ payslip.base.rateUnit === 'day' ? 'días' : 'horas' }}</span>
                <span class="ps-variable__rate">× {{ formatAmount(payslip.base.rate) }}/{{ payslip.base.rateUnit === 'day' ? 'día' : 'hora' }}</span>
              </div>
              <span class="ps-variable__result">{{ formatAmount(editedBase.amount) }}</span>
            </div>
          </div>
        </div>

        <!-- Totals -->
        <div class="ps-section">
          <div class="ps-calc-cell ps-calc-cell--totals">
            <div class="ps-calc-cell__label">Totales</div>
            <div class="balance-line">
              <span class="balance-line__label">Bruto</span>
              <span class="balance-line__value">{{ formatAmount(liveGross) }}</span>
            </div>
            <div v-if="liveDeductions > 0" class="balance-line">
              <span class="balance-line__label">Descuentos</span>
              <span class="balance-line__value balance-line__value--error">-{{ formatAmount(liveDeductions) }}</span>
            </div>
            <div class="balance-line balance-line--result">
              <span class="balance-line__label">Neto</span>
              <span class="balance-line__value">{{ formatAmount(liveNet) }}</span>
            </div>
          </div>
        </div>

        <!-- Deposit destination (full width within the grid) -->
        <div class="ps-section ps-grid__full">
          <div class="ps-section__label">Destino de depósito</div>
          <template v-if="hasAccountDeposit">
            <div class="ps-deposit__line">
              <span class="ps-deposit__icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="3" y1="21" x2="21" y2="21"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="5 6 12 3 19 6"/><line x1="4" y1="10" x2="4" y2="21"/><line x1="20" y1="10" x2="20" y2="21"/><line x1="8" y1="14" x2="8" y2="17"/><line x1="12" y1="14" x2="12" y2="17"/><line x1="16" y1="14" x2="16" y2="17"/>
                </svg>
              </span>
              <span>{{ payslip.payment.bank ? payslip.payment.bank + ' · ' : '' }}{{ formatPayMethod(payslip.payment.method) }}<span v-if="payslip.payment.accountNumber"> · {{ payslip.payment.accountNumber }}</span></span>
            </div>
            <div v-if="payslip.payment.cci" class="ps-deposit__cci">CCI {{ payslip.payment.cci }}</div>
          </template>
          <div v-else-if="hasWalletDeposit" class="ps-deposit__line">
            <span class="ps-deposit__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2v-5"/><path d="M23 10h-5a2 2 0 0 0 0 4h5"/>
              </svg>
            </span>
            <span>{{ formatPayMethod(payslip.payment.method) }}<span v-if="payslip.payment.phone"> · {{ payslip.payment.phone }}</span></span>
          </div>
          <p v-else class="ps-deposit__empty">Sin datos de depósito</p>
        </div>
      </div>

      <!-- Adjustments -->
      <div class="ps-section">
        <div class="ps-section__label">Ajustes</div>
        <div v-if="editedAdjustments.length" class="ps-adjustments">
          <!-- Positivos: suman al neto -->
          <div v-if="positiveAdjustments.length" class="ps-adj-group ps-adj-group--pos">
            <div class="ps-adj-group__head">
              <span class="ps-adj-group__title">Suman</span>
              <span class="ps-adj-group__subtotal ps-adj-group__subtotal--pos">+{{ formatAmount(positiveSubtotal) }}</span>
            </div>
            <div v-for="adj in positiveAdjustments" :key="adj.id" class="ps-adj-row">
              <span class="ps-adj-row__kind ps-adj-row__kind--pos">
                <AdjKindIcon :kind="adj.kind" />
                {{ formatKind(adj.kind) }}
              </span>
              <span class="ps-adj-row__label">{{ adj.label }}<span v-if="adj.note" class="ps-adj-row__note"> · {{ adj.note }}</span></span>
              <span class="ps-adj-row__amount ps-adj-row__amount--pos">{{ formatSigned(adj.amount) }}</span>
              <button v-if="isDraft" class="ps-adj-row__del" @click="removeAdjustment(adj.id)" title="Eliminar">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                </svg>
              </button>
            </div>
          </div>
          <!-- Negativos: restan del neto -->
          <div v-if="negativeAdjustments.length" class="ps-adj-group ps-adj-group--neg">
            <div class="ps-adj-group__head">
              <span class="ps-adj-group__title">Restan</span>
              <span class="ps-adj-group__subtotal ps-adj-group__subtotal--neg">-{{ formatAmount(negativeSubtotal) }}</span>
            </div>
            <div v-for="adj in negativeAdjustments" :key="adj.id" class="ps-adj-row">
              <span class="ps-adj-row__kind ps-adj-row__kind--neg">
                <AdjKindIcon :kind="adj.kind" />
                {{ formatKind(adj.kind) }}
              </span>
              <span class="ps-adj-row__label">{{ adj.label }}<span v-if="adj.note" class="ps-adj-row__note"> · {{ adj.note }}</span></span>
              <span class="ps-adj-row__amount ps-adj-row__amount--neg">{{ formatSigned(adj.amount) }}</span>
              <button v-if="isDraft" class="ps-adj-row__del" @click="removeAdjustment(adj.id)" title="Eliminar">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        <p v-else class="ps-adjustments__empty">Sin ajustes</p>

        <!-- Add adjustment form -->
        <div v-if="isDraft">
          <button v-if="!showAdjForm" class="btn btn--sm ps-adj-add" @click="showAdjForm = true">+ Ajuste</button>
          <div v-else class="ps-adj-form">
            <div class="form-group ps-field">
              <label>Tipo</label>
              <select v-model="adjForm.kind" class="select">
                <option value="overtime">Hora extra</option>
                <option value="bonus">Bono</option>
                <option value="deduction">Descuento</option>
                <option value="absence">Falta</option>
                <option value="advance">Adelanto</option>
                <option value="other">Otro</option>
              </select>
            </div>
            <div class="form-group ps-field">
              <label>Concepto</label>
              <input v-model="adjForm.label" class="input" type="text" maxlength="80" placeholder="Descripción…">
            </div>
            <div class="ps-adj-form__row">
              <div class="form-group ps-field ps-field--fill">
                <label>Monto (S/)</label>
                <input v-model.number="adjForm.magnitude" class="input" type="number" min="0" step="0.01" placeholder="0.00">
                <span class="ps-field__hint">{{ adjHint(adjForm.kind) }}</span>
              </div>
              <div class="form-group ps-field ps-field--fill">
                <label>Nota (opcional)</label>
                <input v-model="adjForm.note" class="input" type="text" maxlength="80">
              </div>
            </div>
            <div class="ps-adj-form__actions">
              <button class="btn" @click="addAdjustment">Agregar</button>
              <button class="btn btn--outline" @click="showAdjForm = false">Cancelar</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Notes -->
      <div class="ps-section">
        <div class="ps-section__label">Notas</div>
        <textarea v-if="isDraft" v-model="editedNotes" class="textarea" rows="2" placeholder="Observaciones…" maxlength="300"></textarea>
        <p v-else class="ps-notes-read">{{ payslip.notes || '—' }}</p>
      </div>

      <!-- Deposit voucher(s) — shown once paid; attach/append on finalized -->
      <div v-if="showVoucherSection" class="ps-section">
        <div class="ps-section__label">Voucher del depósito</div>
        <div v-if="existingVouchers.length" class="media-grid ps-vouchers">
          <div v-for="(url, i) in existingVouchers" :key="i" class="media">
            <img :src="url" alt="Voucher del depósito">
            <div class="media__open" @click="openVoucher(url)">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </div>
          </div>
        </div>
        <p v-else-if="canAttachVoucher" class="ps-deposit__empty">Sin voucher adjunto</p>

        <template v-if="canAttachVoucher">
          <input @change="onVoucherChange" ref="voucherInputFinal" class="file" type="file" accept="image/*" multiple>
          <div v-if="voucherStaged.length" class="previews">
            <div v-for="(item, i) in voucherStaged" :key="'p' + i" class="previews__item">
              <img v-if="item.preview" :src="item.preview" alt="Voucher">
              <span class="previews__remove" @click="removeVoucher(i)">&times;</span>
            </div>
          </div>
          <div class="ps-voucher-actions">
            <button v-if="!voucherStaged.length" type="button" class="btn btn--outline btn--sm" @click="$refs.voucherInputFinal.click()">+ Adjuntar voucher</button>
            <template v-else>
              <button type="button" class="btn btn--sm" :disabled="uploadingVoucher" :class="{'is-loading': uploadingVoucher}" @click="attachVouchersToFinalized">
                <span v-if="!uploadingVoucher">Subir voucher</span>
              </button>
              <button type="button" class="btn btn--outline btn--sm" @click="$refs.voucherInputFinal.click()">Agregar más</button>
            </template>
          </div>
        </template>
      </div>

      <!-- Finalize form (inline) -->
      <div v-if="finalizeMode" class="ps-section" ref="finalizeForm">
        <div class="ps-action-form">
          <div class="ps-action-form__title">Registrar pago</div>
          <div class="form-group ps-field">
            <label for="fin-date">Fecha de pago</label>
            <input v-model="finalizeDate" type="date" id="fin-date" class="input">
            <span v-if="!finalizeDate" class="error">Ingresa la fecha de pago</span>
          </div>
          <div class="form-group ps-field">
            <label for="fin-method">Método</label>
            <select v-model="finalizeMethod" class="select" id="fin-method">
              <option value="cash">Efectivo</option>
              <option value="transfer">Transferencia</option>
              <option value="other">Otro</option>
            </select>
          </div>
          <div class="form-group ps-field">
            <label>Voucher del depósito (opcional)</label>
            <input @change="onVoucherChange" ref="voucherInput" class="file" type="file" accept="image/*" multiple>
            <button type="button" class="btn btn--outline btn--w100 ps-voucher-add" @click="$refs.voucherInput.click()">Adjuntar voucher</button>
            <div v-if="voucherStaged.length" class="previews">
              <div v-for="(item, i) in voucherStaged" :key="i" class="previews__item">
                <img v-if="item.preview" :src="item.preview" alt="Voucher">
                <span class="previews__remove" @click="removeVoucher(i)">&times;</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Void form (inline) -->
      <div v-if="voidMode" class="ps-section">
        <div class="ps-action-form">
          <div class="ps-action-form__title">Anular boleta</div>
          <div class="form-group ps-field">
            <label for="void-reason">Motivo de anulación</label>
            <input v-model="voidReason" type="text" id="void-reason" class="input" maxlength="200" placeholder="Descripción del motivo…">
          </div>
        </div>
      </div>
    </template>

    <template v-slot:footer>
      <!-- Draft footer -->
      <template v-if="isDraft && !finalizeMode && !voidMode">
        <button :disabled="saving" :class="{'is-loading': saving}" @click="saveDraft" class="btn">
          <span v-if="!saving">Guardar cambios</span>
        </button>
        <button @click="openFinalize" class="btn btn--outline ps-footer-btn">Finalizar boleta</button>
      </template>

      <!-- Finalize confirm footer -->
      <template v-if="finalizeMode">
        <button :disabled="saving || !finalizeDate" :class="{'is-loading': saving}" @click="confirmFinalize" class="btn">
          <span v-if="!saving">Confirmar pago</span>
        </button>
        <button @click="finalizeMode = false" class="btn btn--outline ps-footer-btn">Cancelar</button>
      </template>

      <!-- Finalized footer -->
      <template v-if="payslip.status === 'finalized' && !voidMode">
        <button @click="voidMode = true" class="btn btn--outline btn--danger-outline">Anular boleta</button>
      </template>

      <!-- Void confirm footer -->
      <template v-if="voidMode">
        <button :disabled="saving" :class="{'is-loading': saving}" @click="confirmVoid" class="btn btn--danger">
          <span v-if="!saving">Confirmar anulación</span>
        </button>
        <button @click="voidMode = false" class="btn btn--outline ps-footer-btn">Cancelar</button>
      </template>
    </template>
  </Navbar>
  <Shadow :class="{'show': showNavbar}"/>
</template>

<script>
import { h } from 'vue'
import Navbar from '@/components/Navbar'
import Shadow from '@/components/Shadow'
import { db, FieldValue, functions, storage } from '@/firebase/firebaseConfig'
import moment from 'moment'

// Feather-style inline paths per adjustment kind (presentation only).
const ADJ_KIND_PATHS = {
  overtime: [['circle', { cx: 12, cy: 12, r: 10 }], ['polyline', { points: '12 6 12 12 16 14' }]],
  bonus: [['polygon', { points: '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2' }]],
  deduction: [['line', { x1: 5, y1: 12, x2: 19, y2: 12 }]],
  absence: [['circle', { cx: 12, cy: 12, r: 10 }], ['line', { x1: 4.93, y1: 4.93, x2: 19.07, y2: 19.07 }]],
  advance: [['line', { x1: 12, y1: 1, x2: 12, y2: 23 }], ['path', { d: 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' }]],
  other: [['circle', { cx: 12, cy: 12, r: 10 }], ['line', { x1: 12, y1: 8, x2: 12, y2: 12 }], ['line', { x1: 12, y1: 16, x2: 12.01, y2: 16 }]]
}

// Small functional component — renders the SVG icon for an adjustment kind in the
// correct namespace (avoids v-html, which the rest of the repo never uses).
const AdjKindIcon = {
  name: 'AdjKindIcon',
  props: { kind: { type: String, default: 'other' } },
  render () {
    const paths = ADJ_KIND_PATHS[this.kind] || ADJ_KIND_PATHS.other
    return h('svg', {
      xmlns: 'http://www.w3.org/2000/svg',
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '2',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round'
    }, paths.map(([tag, attrs]) => h(tag, attrs)))
  }
}

export default {
  name: 'PayslipSidebar',
  components: { Navbar, Shadow, AdjKindIcon },
  props: {
    showNavbar: { type: Boolean, default: false },
    payslip: { type: Object, default: () => ({}) }
  },
  emits: ['close', 'updated', 'finalized', 'voided'],
  data () {
    return {
      editedUnits: 0,
      editedBase: {},
      editedAdjustments: [],
      editedNotes: '',
      showAdjForm: false,
      adjForm: { kind: 'overtime', label: '', magnitude: 0, note: '' },
      finalizeMode: false,
      finalizeDate: '',
      finalizeMethod: 'cash',
      voidMode: false,
      voidReason: '',
      saving: false,
      // Deposit voucher(s) picked but not uploaded yet: one { file, preview }
      // entry per image, so the thumbnail can never drift away from its file.
      voucherStaged: [],
      uploadingVoucher: false
    }
  },
  computed: {
    isDraft () { return this.payslip.status === 'draft' },
    halfLabel () { return (this.payslip.half || 1) === 2 ? '2ª quincena' : '1ª quincena' },
    // Default payment date derived from period + half:
    // 1st half → YYYY-MM-15, 2nd half → last day of the payslip's month.
    defaultPaymentDate () {
      const period = this.payslip.period
      if (!period) return ''
      if ((this.payslip.half || 1) === 1) return `${period}-15`
      const [y, m] = period.split('-').map(Number)
      const lastDay = new Date(y, m, 0).getDate() // day 0 of next month = last day of this month
      return `${period}-${String(lastDay).padStart(2, '0')}`
    },
    sidebarTitle () {
      if (!this.payslip.workerName) return 'Boleta'
      return `${this.payslip.workerName} ${this.payslip.workerLastName || ''}`
    },
    liveGross () {
      let gross = (this.editedBase.amount || 0)
      for (const a of this.editedAdjustments) {
        if ((a.amount || 0) >= 0) gross += (a.amount || 0)
      }
      return this.round2(gross)
    },
    liveDeductions () {
      let ded = 0
      for (const a of this.editedAdjustments) {
        if ((a.amount || 0) < 0) ded += -(a.amount || 0)
      }
      return this.round2(ded)
    },
    liveNet () { return this.round2(this.liveGross - this.liveDeductions) },
    hasAccountDeposit () {
      const p = this.payslip.payment
      return !!p && (p.method === 'savings' || p.method === 'checking')
    },
    hasWalletDeposit () {
      const p = this.payslip.payment
      return !!p && (p.method === 'yape' || p.method === 'plin')
    },
    // Read-only presentation groups over editedAdjustments (source of truth stays the flat array).
    positiveAdjustments () { return this.editedAdjustments.filter(a => (a.amount || 0) >= 0) },
    negativeAdjustments () { return this.editedAdjustments.filter(a => (a.amount || 0) < 0) },
    positiveSubtotal () { return this.round2(this.positiveAdjustments.reduce((s, a) => s + (a.amount || 0), 0)) },
    negativeSubtotal () { return this.round2(-this.negativeAdjustments.reduce((s, a) => s + (a.amount || 0), 0)) },
    // Deposit vouchers already attached to the payslip (Storage download URLs).
    existingVouchers () { return Array.isArray(this.payslip.vouchers) ? this.payslip.vouchers : [] },
    // The voucher block is shown once paid (finalized) or whenever a voucher exists.
    showVoucherSection () { return (this.payslip.status === 'finalized' && !this.voidMode) || this.existingVouchers.length > 0 },
    // Can attach/append vouchers only on a finalized payslip (not while voiding).
    canAttachVoucher () { return this.payslip.status === 'finalized' && !this.voidMode }
  },
  watch: {
    payslip (val) { if (val && val.id) this.syncFromProp() },
    showNavbar (val) { if (val && this.payslip && this.payslip.id) this.syncFromProp() }
  },
  methods: {
    round2 (n) { return Math.round((n + Number.EPSILON) * 100) / 100 },
    formatAmount (n) {
      return 'S/ ' + Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0)
    },
    // Signed amount with the sign leading the currency (+S/ 150.00 / -S/ 80.00),
    // same shape the group subtotals use — `formatAmount` alone would render a
    // negative as "S/ -80.00" and break the column's alignment.
    formatSigned (n) {
      return ((n || 0) < 0 ? '-' : '+') + this.formatAmount(Math.abs(n || 0))
    },
    formatDate (ts) {
      if (!ts) return '—'
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return moment(d).locale('es').format('DD/MMM/YY')
    },
    formatKind (k) {
      const m = { overtime: 'Extra', bonus: 'Bono', deduction: 'Descuento', absence: 'Falta', advance: 'Adelanto', other: 'Otro' }
      return m[k] || k
    },
    formatMethod (m) {
      const map = { cash: 'Efectivo', transfer: 'Transferencia', other: 'Otro' }
      return map[m] || m
    },
    formatPayMethod (m) {
      const map = { savings: 'Ahorros', checking: 'Corriente', yape: 'Yape', plin: 'Plin' }
      return map[m] || m
    },
    syncFromProp () {
      const p = this.payslip
      this.editedBase = p.base ? { ...p.base } : {}
      this.editedUnits = p.base ? (p.base.units || 0) : 0
      this.editedAdjustments = p.adjustments ? p.adjustments.map(a => ({ ...a })) : []
      this.editedNotes = p.notes || ''
      this.finalizeMode = false
      this.voidMode = false
      this.finalizeDate = ''
      this.finalizeMethod = 'cash'
      this.voidReason = ''
      this.showAdjForm = false
      this.adjForm = { kind: 'overtime', label: '', magnitude: 0, note: '' }
      this.voucherStaged = []
      this.uploadingVoucher = false
    },
    onUnitsInput () {
      if (this.editedBase.kind === 'variable') {
        this.editedBase = {
          ...this.editedBase,
          units: this.editedUnits,
          amount: this.round2((this.editedUnits || 0) * (this.editedBase.rate || 0))
        }
      }
    },
    signFor (kind) {
      return ['deduction', 'absence', 'advance'].includes(kind) ? -1 : 1
    },
    adjHint (kind) {
      return this.signFor(kind) === -1 ? '- Se restará del neto' : '+ Se sumará al neto'
    },
    addAdjustment () {
      if (!this.adjForm.label.trim()) { this.$toast.error('Ingresa un concepto'); return }
      if (!this.adjForm.magnitude && this.adjForm.magnitude !== 0) { this.$toast.error('Ingresa el monto'); return }
      const id = Date.now().toString(36) + Math.random().toString(36).substr(2, 5)
      const amount = this.round2(this.signFor(this.adjForm.kind) * Math.abs(this.adjForm.magnitude || 0))
      const adj = { id, kind: this.adjForm.kind, label: this.adjForm.label.trim(), amount }
      const note = this.adjForm.note.trim()
      if (note) adj.note = note
      this.editedAdjustments.push(adj)
      this.adjForm = { kind: 'overtime', label: '', magnitude: 0, note: '' }
      this.showAdjForm = false
    },
    removeAdjustment (id) {
      this.editedAdjustments = this.editedAdjustments.filter(a => a.id !== id)
    },
    openFinalize () {
      // Prefill the payment date with the half's default (day 15 / end of month).
      this.finalizeDate = this.defaultPaymentDate
      this.finalizeMode = true
      // The form renders under the adjustments, so on a long payslip it opens
      // below the fold and only the footer visibly changes — the payment date
      // and the voucher picker would go unnoticed. Bring it into view.
      this.$nextTick(() => {
        const form = this.$refs.finalizeForm
        if (form && form.scrollIntoView) form.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
    },
    async _persistDraft () {
      const updates = {
        base: { ...this.editedBase },
        adjustments: this.editedAdjustments.map(a => ({ ...a })),
        notes: this.editedNotes,
        grossTotal: this.liveGross,
        deductionsTotal: this.liveDeductions,
        netTotal: this.liveNet
      }
      await db.collection('payslips').doc(this.payslip.id).update({ ...updates, updatedAt: FieldValue.serverTimestamp() })
      // Emit the plain values only — the serverTimestamp sentinel is a write
      // token, not a date, and would end up sitting in the parent's list state.
      this.$emit('updated', { id: this.payslip.id, ...updates })
    },
    async saveDraft () {
      try {
        this.saving = true
        await this._persistDraft()
        this.$toast.success('Boleta guardada')
        this.$emit('close')
      } catch (e) {
        console.error(e)
        this.$toast.error('Error al guardar boleta')
      } finally {
        this.saving = false
      }
    },
    async confirmFinalize () {
      if (!this.finalizeDate) { this.$toast.error('Ingresa la fecha de pago'); return }
      try {
        this.saving = true
        await this._persistDraft()
        // Upload any staged deposit voucher(s) and attach them to the payslip.
        if (this.voucherStaged.length) {
          const urls = await this.uploadStagedVouchers()
          const merged = [...this.existingVouchers, ...urls]
          await db.collection('payslips').doc(this.payslip.id).update({ vouchers: merged })
        }
        const fn = functions.httpsCallable('planilla-finalizePayslip')
        const res = await fn({ payslipId: this.payslip.id, paymentDate: this.finalizeDate, paymentMethod: this.finalizeMethod })
        if (res.data.ok) {
          this.$emit('finalized', this.payslip.id)
          this.$toast.success('Boleta finalizada')
          this.$emit('close')
        }
      } catch (e) {
        console.error(e)
        this.$toast.error(e.message || 'Error al finalizar')
      } finally {
        this.saving = false
      }
    },
    async confirmVoid () {
      try {
        this.saving = true
        const fn = functions.httpsCallable('planilla-voidPayslip')
        const res = await fn({ payslipId: this.payslip.id, reason: this.voidReason })
        if (res.data.ok) {
          this.$emit('voided', this.payslip.id)
          this.$toast.success('Boleta anulada')
          this.$emit('close')
        }
      } catch (e) {
        this.$toast.error(e.message || 'Error al anular')
      } finally {
        this.saving = false
      }
    },
    // ── Deposit voucher(s) ──────────────────────────────────────
    // Same upload pattern as bills/Pay.vue: image/* to Storage vouchers/, keep URLs.
    onVoucherChange (e) {
      if (!e.target.files) return
      let rejected = 0
      Array.from(e.target.files).forEach(file => {
        if (!file.type.startsWith('image/')) { rejected++; return }
        // One entry per file, created up front; the reader writes the preview
        // back into *its own* entry. Pushing previews into a parallel array
        // would reorder them — readers finish by file size, not by pick order —
        // and then removing a thumbnail would drop a different voucher.
        const entry = this.voucherStaged[this.voucherStaged.push({ file, preview: '' }) - 1]
        const reader = new FileReader()
        reader.onload = ev => { entry.preview = ev.target.result }
        reader.readAsDataURL(file)
      })
      if (rejected) this.$toast.error('Solo se pueden adjuntar imágenes')
      e.target.value = ''
    },
    removeVoucher (index) {
      this.voucherStaged.splice(index, 1)
    },
    uploadVoucherFile (file) {
      return new Promise((resolve, reject) => {
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
        const name = Math.random().toString(36).substr(1, 20) + '.' + ext
        storage.ref(`vouchers/${name}`).put(file)
          .then(snap => snap.ref.getDownloadURL().then(resolve))
          .catch(reject)
      })
    },
    uploadStagedVouchers () {
      if (!this.voucherStaged.length) return Promise.resolve([])
      return Promise.all(this.voucherStaged.map(s => this.uploadVoucherFile(s.file)))
    },
    openVoucher (url) { window.open(url, '_blank') },
    // Append vouchers to an already-finalized payslip (admin can write directly).
    async attachVouchersToFinalized () {
      if (!this.voucherStaged.length) return
      try {
        this.uploadingVoucher = true
        const urls = await this.uploadStagedVouchers()
        const merged = [...this.existingVouchers, ...urls]
        await db.collection('payslips').doc(this.payslip.id).update({
          vouchers: merged,
          updatedAt: FieldValue.serverTimestamp()
        })
        this.$emit('updated', { id: this.payslip.id, vouchers: merged })
        this.voucherStaged = []
        this.$toast.success('Voucher adjuntado')
      } catch (e) {
        console.error(e)
        this.$toast.error('Error al subir el voucher')
      } finally {
        this.uploadingVoucher = false
      }
    },
    close () {
      this.finalizeMode = false
      this.voidMode = false
      this.$emit('close')
    }
  }
}
</script>
